/**
 * cost — leitura/escrita do CUSTO (compra ou produção) de um item de catálogo.
 *
 * Por que um módulo só para isso: `products.cost` é **nullable de propósito** e
 * a diferença entre `null` ("não informado") e `0` ("de graça") é o eixo de toda
 * esta feature. `Number(null) === 0` e `Number('') === 0` transformam a ausência
 * em zero sem erro nenhum (armadilha #52/#35c do CLAUDE.md), o que inflaria
 * margem e subestimaria o capital investido. Então a conversão vive num lugar
 * único, puro e testado.
 *
 * Não confundir com PREÇO: `products.price` é NOT NULL e é o que o cliente paga.
 * Custo é o que o prestador gasta, e o app viveu sem ele até aqui.
 */

import { numberToMaskedBRL } from '@/lib/currencyInput';

/** Resultado de ler um campo de custo digitado pelo usuário. */
export interface ParsedCost {
  /** `false` só quando o texto é inválido (não-número ou negativo). */
  ok: boolean;
  /**
   * `null` quando o campo está VAZIO — isto é um valor válido ("não informado"),
   * não um erro. Também é `null` quando `ok` é `false`.
   */
  value: number | null;
}

/**
 * ─── O CAMPO DE CUSTO É MASCARADO, e por isso o parser mudou ────────────────
 *
 * Até 2026-08-08 o custo era digitação livre lida por `parseFloat(replace(',',
 * '.'))`. Isso errava por MIL num valor grande escrito como se escreve em pt-BR:
 * `"1.500,00"` virava `"1.500.00"` e o `parseFloat` parava no segundo ponto →
 * **R$ 1,50**. É o D7 (#151a/#157) no último campo de dinheiro que ainda não
 * tinha máscara.
 *
 * A cura não é um parser mais esperto — nenhum parser resolve a ambiguidade do
 * PONTO (em pt-BR ele é milhar; no teclado do celular sai como decimal). Quem
 * resolve é a MÁSCARA (`MoneyTextInput`), que não deixa digitar separador
 * nenhum: os dígitos entram pela direita, em centavos.
 *
 * ⚠️ `parseCostInput` e `formatCostInput` andam SEMPRE juntos (#153a). Formatar
 * "18,5" e ler com o parser novo daria 18,50 por acaso; formatar "1500" e ler
 * daria 1500 em vez de 15,00. Os dois falam o MESMO formato mascarado.
 */

/**
 * Lê um custo digitado em pt-BR mascarado ("1.500,00"). Campo vazio é válido e
 * vira `null`. Negativo é recusado (não existe custo negativo — e o banco tem
 * CHECK).
 */
export function parseCostInput(raw: string | number | null | undefined): ParsedCost {
  if (typeof raw === 'number') {
    if (!Number.isFinite(raw) || raw < 0) return { ok: false, value: null };
    return { ok: true, value: raw };
  }

  const text = String(raw ?? '').trim();
  if (!text) return { ok: true, value: null };

  // Recusa qualquer coisa que não seja um número pt-BR: sem isto `Number` aceita
  // `'0x10'` como 16 e `'1e3'` como 1000 (#123a), que viram custo plausível e
  // não disparam nenhum alarme.
  if (!/^\d{1,3}(\.\d{3})*(,\d+)?$|^\d+(,\d+)?$/.test(text)) return { ok: false, value: null };

  const n = Number(text.replace(/\./g, '').replace(',', '.'));
  if (!Number.isFinite(n) || n < 0) return { ok: false, value: null };
  return { ok: true, value: n };
}

/**
 * Custo do banco → texto MASCARADO do formulário. `null` vira campo VAZIO,
 * nunca "0" — a distinção que sustenta a coluna nullable (#61).
 */
export function formatCostInput(cost: number | null | undefined): string {
  if (cost == null) return '';
  const n = Number(cost);
  if (!Number.isFinite(n)) return '';
  return numberToMaskedBRL(n);
}

/**
 * Custo de um produto do catálogo, normalizado. Aceita a linha crua do banco
 * (onde `numeric` pode chegar como string) e devolve `null` para qualquer coisa
 * que não seja um número utilizável — inclusive `0`? **Não**: zero é um custo
 * legítimo (item de brinde/doado) e é preservado. Só a AUSÊNCIA vira `null`.
 */
export function productUnitCost(
  product: { cost?: number | string | null } | null | undefined,
): number | null {
  const raw = product?.cost;
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return null;
  return n;
}

/** Mensagem única do toast de custo inválido (pt-BR). */
export const INVALID_COST_MESSAGE = 'Informe um custo válido (ou deixe em branco).';

/** Mensagem de custo inválido de uma variação específica. */
export function invalidVariantCostMessage(variantLabel: string): string {
  return `Informe um custo válido para a variação "${variantLabel}" (ou deixe em branco).`;
}
